/**
 * t627u: Print label / Print labels for equipment. Pick a size (76 × 51 or 150 × 100 mm thermal, A4 Avery L7160 or
 * L7163; the last one used is remembered on this device, and a retired size becomes 76 × 51), then the labels
 * download as a PDF made in the browser. The codes are the items' QR points (made the first time). A label is just
 * the QR code and the kit's details (no logo or workspace name). Settings › QR codes prints any codes the same way
 * (`points`: what each one is and where).
 */
import { useState } from 'react';
import QRCode from 'qrcode';
import clsx from 'clsx';
import { QR_LABEL_SIZES, equipmentQrLabel, qrLabelSizeOr, qrLabelsPdf, qrPointLabel, type Equipment, type QrLabelSize, type QrPoint } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Button, Modal } from '@/components/ui';
import { useMobileLayout } from '@/mobile/layout';
import { BottomSheet } from '@/mobile/BottomSheet';

const SIZE_KEY = 'hub.qrLabelSize';
const lastSize = (): QrLabelSize => {
  try { return qrLabelSizeOr(localStorage.getItem(SIZE_KEY)); } catch { return qrLabelSizeOr(null); } // private mode
};
const matrix = (text: string) => {
  const q = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const n = q.modules.size;
  return Array.from({ length: n }, (_, y) => Array.from({ length: n }, (_, x) => Boolean(q.modules.get(y, x))));
};

function download(bytes: Uint8Array, name: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${name}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Makes sure each item has its code, then downloads the labels. Returns how many were printed. */
export async function printEquipmentLabels(ws: string, items: Equipment[], size: QrLabelSize): Promise<number> {
  const points = items.length === 1 ? [await api.ops.equipmentQr(ws, items[0]!.id)] : await api.ops.equipmentQrLabels(ws, items.map(e => e.id));
  const code = new Map(points.map(p => [p.equipment_id, p.code]));
  const origin = window.location.origin;
  const labels = items.flatMap(e => (code.get(e.id) ? [equipmentQrLabel(e, code.get(e.id)!, origin)] : []));
  if (!labels.length) return 0;
  const name = items.length === 1 ? `label-${items[0]!.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}` : `equipment-labels-${labels.length}`;
  download(qrLabelsPdf(labels, matrix, size), name);
  return labels.length;
}

/** Settings › QR codes: labels for codes that already exist (nothing to make first). */
export function printQrPointLabels(points: QrPoint[], size: QrLabelSize): number {
  if (!points.length) return 0;
  const origin = window.location.origin;
  const name = points.length === 1 ? `label-${points[0]!.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}` : `qr-labels-${points.length}`;
  download(qrLabelsPdf(points.map(p => qrPointLabel(p, origin)), matrix, size), name);
  return points.length;
}

/** Print label(s): the size choice, then Print / Cancel. Desktop modal; a bottom sheet in the phone layout. */
export function PrintLabelsDialog({ items = [], points, onClose, onPrinted }: { items?: Equipment[]; points?: QrPoint[]; onClose: () => void; onPrinted?: (n: number) => void }) {
  const wsId = useWorkspaceId();
  const phone = useMobileLayout();
  const toast = useUi(s => s.toast);
  const [size, setSize] = useState<QrLabelSize>(lastSize);
  const [busy, setBusy] = useState(false);
  const count = points ? points.length : items.length;
  const title = count === 1 ? 'Print label' : `Print ${count} labels`;
  const print = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      try { localStorage.setItem(SIZE_KEY, size); } catch { /* private mode */ }
      const n = points ? printQrPointLabels(points, size) : await printEquipmentLabels(wsId, items, size);
      if (!n) toast(points ? 'Nothing to print' : 'Nothing to print: you can only print labels for equipment you manage', 'error');
      else { if (count > 1) toast(`${n} label${n === 1 ? '' : 's'} ready`, 'success'); onPrinted?.(n); onClose(); }
    } catch (err) { toast(errorMessage(err, 'Couldn’t make the labels'), 'error'); }
    finally { setBusy(false); }
  };
  const body = (
    <div role="radiogroup" aria-label="Label size" className="space-y-1" data-testid="labels-sizes">
      {QR_LABEL_SIZES.map(s => (
        <label key={s.id} className={clsx('flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2', size === s.id ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50')}>
          <input type="radio" name="label-size" value={s.id} checked={size === s.id} onChange={() => setSize(s.id)} data-testid={`labels-size-${s.id}`} />
          <span className="min-w-0"><span className="block text-sm font-medium">{s.name}</span><span className="block text-xs text-slate-500">{s.hint}</span></span>
        </label>
      ))}
      <p className="px-3 pt-2 text-xs text-slate-500">{points
        ? 'Each label has the QR code, its name, the building and where exactly, and what scanning it does (a vehicle’s has its registration). The code stays the same, so reprinting doesn’t stop old labels working. Print at 100% (actual size).'
        : 'Each label has the QR code, name, ID, make and model and building (the 150 × 100 mm label adds where it lives and its category). Print at 100% (actual size).'}</p>
    </div>
  );
  const footer = <>
    <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="labels-cancel">Cancel</Button>
    <Button onClick={() => void print()} loading={busy} data-testid="labels-print">Print</Button>
  </>;
  return phone
    ? <BottomSheet open onClose={onClose} title={title} testId="labels-sheet" footer={<div className="flex justify-end gap-2 pb-1">{footer}</div>}><div className="px-2 pb-2">{body}</div></BottomSheet>
    : <Modal open onClose={onClose} title={title} footer={footer}>{body}</Modal>;
}
